import { Component, OnInit } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';

import { Notification as Notice, Notification_Service, SuccessAccount_Service } from 'legalreflex-lib';
import { NotificationPopupDialog } from '../../routes/notification/dialog/notificationPopup';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ReflexEnvironment as ENV } from '@smartsoftware/reflex-core';
import { identifyUser, track } from '../../analytics';

declare global {
	interface Window { _mfq: any; }
}

@Component({
	selector: 'user-control',
	templateUrl: './userControl.html',
	styleUrls: ['./userControl.scss']
})
export class UserControlComponent implements OnInit {
	public recentNotifications: Notice[] = []
    public notificationLength: number = -1;
    public interval: number | undefined;
    protected lastNotificationCall: Date = new Date();
    public waitTime: number = (1000 * 60 * 5); // [SCS-1703] increased from 3 minutes to 5 minutes
    private currentDialog?: MatDialogRef<NotificationPopupDialog>;
	public mouseFlowStarted: boolean = false;

	constructor(
		public auth: OidcSecurityService,
		public userService: SuccessAccount_Service,

		public notificationService : Notification_Service,
        protected dialog: MatDialog,
        protected snackbar: MatSnackBar

	) {
		this.recentNotifications = [];
        sessionStorage.setItem("filterCollapsed", "true")
	}

	ngOnInit() {
		let authURL = window.location.pathname.includes('impersonate') ? window.location.origin : window.location.href;
        // checkAuth is expecting the start of an authorization code flow. It will check for the auth code in the query string params
        // seems if the user is not authenticated, and the code is missing from the query string, and the window location is not /, it will navigate to /, and therefore
        // the impersonate component will never get loaded (which means impersonateService.doImpersonate will never get called). 
        // Giving an explicit url seems to fix this behaviour.
		this.auth.checkAuth(authURL)
			.subscribe((response) => {
                console.debug("userControl: ngOnInit: checkAuth: isAuthenticated:", response.isAuthenticated);
                console.debug('userControl: ngOnInit: mouseFlowStarted:', this.mouseFlowStarted);
                if(!this.mouseFlowStarted && response.isAuthenticated) {
					this.startMouseFlow(response.userData.sub);
                }
			});
            
        if(this.auth.isAuthenticated()) {
            document.addEventListener("visibilitychange", this.visibilityChangeListener);
            this.startNotificationPolling();
            this.auth.userData$.subscribe(({userData}) => this.identifySignedInUser(userData as Record<string, any>));
            this.identifySignedInUser(null);
        }
    }

    identifySignedInUser(userData: Record<string, unknown> | null): void {
        identifyUser(Object.assign({}, this.auth.getPayloadFromIdToken(), userData));
    }

	login() {
        track('User Started Login');
		this.auth.authorize();
	}

	logout(): void {
		this.auth.logoff();
	}

    get totalNotices(){
        return this.notificationLength 
    }

	getNotifications(manual?:boolean, showSnackMessage: boolean = true) {
        let lastTime: Date;
        if (manual) {
            lastTime = new Date();
            lastTime.setDate(lastTime.getDate() - 7);
        } else {
            lastTime = new Date(this.lastNotificationCall);
        } 
        lastTime.setHours(lastTime.getHours() - 8); // because pacific time
        this.notificationService.getRecentNotifications({
            notificationLimit:10,
            includeViewed:false, 
            createdAt: lastTime
        }).subscribe((entities)=>{

            this.lastNotificationCall = new Date()
            if(manual && showSnackMessage){
                if(entities.length <= 0)
                this.snackbar.open('No new notifications in the last 7 days found.', undefined, { duration: 4000, verticalPosition: 'top', horizontalPosition: 'right', panelClass: 'snack-error' })
            }
            entities.notifications.forEach((notice:Notice)=>{
                if(Notification.permission == 'granted'){
                    let recentNotice = new Notification(notice.subject? notice.subject: "",
                    {
                        body:notice.contentText ? notice.contentText: undefined,
                        data:notice.data
                    })

                    notice.deliveredBrowserPushNotification = true;
                    this.notificationService.push(notice).subscribe();
                    
                    recentNotice.onclick = () => {
                        this.openNotification(notice)
                    }
                }
            })
            this.recentNotifications = entities.notifications
            this.notificationLength = entities.length
            sessionStorage.setItem("userNotifications", JSON.stringify(this.recentNotifications))
        })
    }

    visibilityChangeListener = (e: Event) => {
        console.debug('visibilitychange emiitted', e);
        console.debug('document.visibilityState', document.visibilityState);
        if(document.visibilityState !== "visible"){
            this.stopNotificationPolling(); 
        }
        else{
            this.startNotificationPolling();
        }
    }

    startNotificationPolling() {
        if (this.interval) {
            console.warn('already started');
            return;
        }
        this.getNotifications(true, false); // initial fetch for notifications
        console.debug('Starting polling interval for newly received notifications');
        this.interval = window.setInterval((()=> this.getNotifications(false, false)), this.waitTime )
        // listen for visibility change to start and stop polling.
        //document.addEventListener("visibilitychange", this.visibilityChangeListener);
    }
    
    stopNotificationPolling() {
        if (!this.interval) {
            console.warn('already stopped');
            return;
        }
        console.debug('Stopping polling interval for newly received notifications')
        clearInterval(this.interval);
        this.interval = undefined;
        //document.removeEventListener('visibilitychange', this.visibilityChangeListener);
    }

    // [SCS-1703] added lookup for notifcation since the list query no longer gets all the data.
    public async openNotification(entity:Notice) {
        let notification = await this.notificationService.getNotification(entity.uuid).toPromise();
        let ind = this.recentNotifications.findIndex((notice)=>{return notice.uuid == entity.uuid || notice.id == entity.id})
        if(ind != -1){
            this.recentNotifications.splice(ind,1)
            this.notificationLength -= 1
        }
        window.focus()

        let previousDialog: MatDialogRef<NotificationPopupDialog> | undefined;
        if (this.currentDialog) {
            previousDialog = this.currentDialog;
        } 
        this.currentDialog = this.dialog.open(NotificationPopupDialog, {
            data: notification
        })
        this.currentDialog.afterOpened().subscribe(() => {
            if (previousDialog) {
                previousDialog.close();
                previousDialog = undefined;
            }
        });
        this.currentDialog.afterClosed().subscribe((res)=>{
            if(res){
                // acknowledge / dismiss notification
                Object.assign(notification, res.data)
                this.notificationService.push(notification).subscribe()
            }
        })
    }

	public startMouseFlow(username: string) {
        window._mfq = window._mfq || [];
        let tag = window.sessionStorage.getItem(`Impersonate`)? "Impersonating: " : "";
        if(tag && window.sessionStorage.getItem(`Impersonate`)==username) {
            window._mfq.push(["tag", tag + username]);
        } else {
            window._mfq.push(["tag", username]);
        }
        
        var mf = document.createElement("script");
        mf.id = "mouseFlow";
        mf.type = "text/javascript"; 
        mf.defer = true;
        mf.src = ENV.config['MouseFlowUrl'];
        document.getElementsByTagName("head")[0].appendChild(mf);
        console.debug('userControl: startMouseFlow: window._mfq:', window._mfq);
        this.mouseFlowStarted = true;
	}
}